/**
 * Phase 1.7 — left primary sidebar.
 *
 * Behavior by breakpoint:
 *   - md+ (>=768px): fixed 240px, always visible. AppShell offsets the
 *     content via md:pl-sidebar-w to make room.
 *   - <md (mobile): hidden by default; slides in as a drawer when the
 *     header's hamburger toggles `sidebarOpen` in HeaderContext. A
 *     backdrop covers the page; tapping it, tapping a nav link, hitting
 *     ESC, or navigating to a new route all dismiss the drawer. Body
 *     scroll is locked while the drawer is open.
 *
 * Implementation note — prior versions used `hidden md:flex` and the
 * hamburger toggled a context flag that nothing was listening to. The
 * drawer state now lives entirely on this component via transforms,
 * keeping the desktop layout identical (`md:translate-x-0`) while
 * giving mobile a real disclosure.
 */

import {
  LayoutDashboard,
  Truck,
  Bug,
  Fence,
  Wrench,
  Flower2,
  Cog,
  Package,
  ScrollText,
  FileText,
  Tags,
  FileBadge,
  Sprout,
  CalendarRange,
  Settings as SettingsIcon,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/lib/auth';
import { useHeader } from './Header';

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', href: '/', icon: LayoutDashboard }],
  },
  {
    label: 'Assets',
    items: [
      { label: 'Animals', href: '/animals', icon: Bug },
      { label: 'Vehicles', href: '/vehicles', icon: Truck },
      { label: 'Pastures', href: '/pastures', icon: Fence },
      { label: 'Tools', href: '/tools', icon: Wrench },
      { label: 'Implements', href: '/implements', icon: Cog },
      { label: 'Gardens', href: '/gardens', icon: Flower2 },
    ],
  },
  {
    label: 'Crops',
    items: [
      { label: 'Crops', href: '/crops', icon: Sprout },
      { label: 'Plantings', href: '/plantings', icon: Sprout },
      { label: 'Crop Planner', href: '/crops/planner', icon: CalendarRange },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Inventory', href: '/inventory', icon: Package },
      { label: 'Contacts', href: '/contacts', icon: Users },
      { label: 'Templates', href: '/templates', icon: FileBadge },
      { label: 'Logs', href: '/logs', icon: ScrollText },
    ],
  },
  {
    label: 'Documents',
    items: [
      { label: 'All Documents', href: '/documents', icon: FileText },
      { label: 'Taxonomy', href: '/taxonomy', icon: Tags },
    ],
  },
];

const SETTINGS_ITEM: NavItem = { label: 'Settings', href: '/settings', icon: SettingsIcon };

export function Sidebar(): JSX.Element {
  const ranchName = useAuthStore((s) => s.ranch?.name ?? 'HollerHub');
  const { sidebarOpen, closeSidebar } = useHeader();
  const location = useLocation();

  // Auto-close on route change so tapping a nav link on mobile dismisses
  // the drawer (the link click navigates, then this effect fires).
  // Intentionally only depends on pathname — closeSidebar is stable.
  useEffect(() => {
    closeSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // While the drawer is open on mobile: lock body scroll and listen for
  // ESC. We feature-detect mobile via matchMedia rather than a window
  // width literal to stay in sync with the md breakpoint.
  useEffect(() => {
    if (!sidebarOpen) return;
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return;

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeSidebar();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [sidebarOpen, closeSidebar]);

  return (
    <>
      {/* Mobile backdrop — only interactive when the drawer is open. */}
      <div
        aria-hidden="true"
        onClick={closeSidebar}
        className={cn(
          'fixed inset-0 z-30 bg-black/50 transition-opacity duration-200 md:hidden',
          sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        id="primary-sidebar"
        aria-label="Primary navigation"
        className={cn(
          // Layout — fixed panel at left, full height, drawer width.
          'fixed inset-y-0 left-0 z-40 flex w-sidebar-w max-w-[85vw] flex-col',
          'border-r border-border bg-surface dark:bg-surface-dark dark:border-border',
          // Mobile transform — hidden off-canvas unless sidebarOpen.
          'transition-transform duration-200 ease-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop — always visible, drop transition cost.
          'md:translate-x-0 md:transition-none md:z-30',
        )}
      >
        <BrandHeader ranchName={ranchName} />
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV_GROUPS.map((g) => (
            <NavGroupBlock key={g.label} group={g} onNavigate={closeSidebar} />
          ))}
        </nav>
        <div className="border-t border-border p-2 dark:border-border">
          <NavRow item={SETTINGS_ITEM} onNavigate={closeSidebar} />
        </div>
      </aside>
    </>
  );
}

function BrandHeader({ ranchName }: { ranchName: string }): JSX.Element {
  return (
    <div className="flex h-header-h items-center gap-3 border-b border-border px-4 dark:border-border">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
        className="h-8 w-8 flex-shrink-0 text-forest dark:text-forest-dark"
        role="img"
        aria-label="HollerHub"
      >
        <rect x="1.75" y="1.75" width="60.5" height="60.5" rx="6" ry="6" fill="none" stroke="currentColor" strokeWidth="3.5" />
        <g fill="currentColor">
          <rect x="18.5" y="18" width="6" height="28" />
          <rect x="39.5" y="18" width="6" height="28" />
          <rect x="18.5" y="29" width="27" height="6" />
        </g>
      </svg>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="font-display text-sm text-forest dark:text-forest-dark">
          Holler<span className="italic text-clay dark:text-clay-dark">Hub</span>
        </span>
        <span className="truncate font-mono text-2xs uppercase tracking-wider text-muted-foreground">
          {ranchName}
        </span>
      </div>
    </div>
  );
}

function NavGroupBlock({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }): JSX.Element {
  return (
    <div className="mb-4">
      <p className="px-3 py-1 font-mono text-sm uppercase tracking-wider text-muted-foreground">
        {group.label}
      </p>
      <ul className="mt-1 space-y-0.5">
        {group.items.map((i) => (
          <li key={i.href}>
            <NavRow item={i} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate: () => void }): JSX.Element {
  const location = useLocation();
  const active = item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href);
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={cn(
        // text-base = font-size 1rem; leading-5 = line-height 1.25rem.
        // Tailwind's text-base ships with line-height 1.5rem by default,
        // so we override with leading-5 to keep the row tight inside h-9.
        'group relative flex h-9 items-center gap-2 rounded-md px-3 text-base leading-5 transition-colors',
        active
          ? 'bg-surface-raised font-medium text-charcoal dark:bg-surface-raised-dark dark:text-charcoal-dark'
          : 'text-muted-foreground hover:bg-surface-raised hover:text-charcoal dark:hover:bg-surface-raised-dark dark:hover:text-charcoal-dark',
      )}
    >
      {active ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-burnt-orange dark:bg-burnt-orange-dark"
        />
      ) : null}
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}
